import PrintIcon from "@mui/icons-material/Print";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import Header from "../layout/header4";
import Footer from "../layout/footer";
import ProductSlider from "../element/productSlider";
import ProductGallerySlider from "../element/productGallerySlider";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { useTranslation } from "react-i18next";

const bg = require("../../images/banner/banner.jpg");

const ProductDetails = () => {
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const { id } = useParams();

  const [loadedImages, setLoadedImages] = useState({});

  const decorationDetailsStyle = {
    display: "flex",
    gap: "20px",
  };

  const headingStyle = {
    margin: "0",
  };


  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${apiUrl}/products/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        setProduct(data.product);
        setLoadedImages({});
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    fetchProduct();
  }, [id, apiUrl]);

  const handleImageLoad = (imageIndex) => {
    setLoadedImages((prev) => ({
      ...prev,
      [imageIndex]: true,
    }));
  };

  const isEmptyValue = (value) => !value && value !== 0;

  const renderCharacteristics = () => {
    if (!product?.characteristics) {
      return (
        <tr>
          <td colSpan={2}>{t("productDetails.noCharacteristics")}</td>
        </tr>
      );
    }

    const characteristicEntries = Object.entries(product.characteristics)
      .filter(
        ([key, value]) =>
          !isEmptyValue(value) &&
          ![
            "seau_iml",
            "seau_offset",
            "couvercle_iml",
            "couvercle_offset",
          ].includes(key)
      )
      .map(([key, value]) => [
        key,
        typeof value === "object" ? value.label : value,
      ]);

    if (characteristicEntries.length === 0) {
      return (
        <tr>
          <td colSpan={2}>{t("productDetails.noCharacteristics")}</td>
        </tr>
      );
    }

    return characteristicEntries.map(([key, value], index) => (
      <tr key={index}>
        <td>{formatCharacteristicKey(key)}</td>
        <td>{String(value)}</td>
      </tr>
    ));
  };

  const formatCharacteristicKey = (key) =>
    key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const isMetallique =
    product?.category &&
    (typeof product?.category === "object"
      ? product?.category.name === "Méttalique"
      : product?.category === "Méttalique");

  if (error)
    return (
      <div>
        {t("productDetails.error")}: {error}
      </div>
    );
  if (!product) return <div>{t("productDetails.notFound")}</div>;

  const handleDownloadPDF = () => {
    if (product?.pdfFiles?.length > 0) {
      const pdfFileUrl = apiUrl + product.pdfFiles[0];
      const link = document.createElement("a");
      link.href = pdfFileUrl;
      link.download = "product.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert(t("productDetails.noPdf"));
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {product.name} - {t("productDetails.metaTitle")}
        </title>
        <meta
          name="description"
          content={`${t("productDetails.metaDescription", {
            name: product.name,
            description: product.description || "",
          })}`}
        />
        <meta
          name="keywords"
          content={`${product.name}, ${t("productDetails.metaKeywords")}, ${
            product.reference || ""
          }`}
        />
        <meta name="author" content="Votre Société" />
        <meta
          property="og:title"
          content={`${product.name} - ${t("productDetails.metaTitle")}`}
        />
        <meta
          property="og:description"
          content={t("productDetails.metaDescription", {
            name: product.name,
            description: product.description || "",
          })}
        />
        <meta property="og:type" content="product" />
        <meta
          property="og:url"
          content={`https://www.gargouri-emballages.com/product-details/${product._id}`}
        />
        <meta
          property="og:image"
          content={
            product.images?.[0] ||
            "https://www.facebook.com/photo/?fbid=1093841022751228&set=a.433081668827170"
          }
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${product.name} - ${t("productDetails.metaTitle")}`}
        />
        <meta
          name="twitter:description"
          content={t("productDetails.metaDescription", {
            name: product.name,
            description: product.description || "",
          })}
        />
        <meta
          name="twitter:image"
          content={
            product.images?.[0] ||
            "https://www.gargouri-emballages.com/logo.png"
          }
        />
      </Helmet>

      <Header />

      <div className="page-content bg-white">
        <div
          className="dlab-bnr-inr overlay-black-middle bg-pt"
          style={{ backgroundImage: `url(${bg})` }}
        >
          <div className="container">
            <div className="dlab-bnr-inr-entry">
              <h1 className="text-white">{t("productDetails.title")}</h1>
              <div className="breadcrumb-row">
                <ul className="list-inline">
                  <li>
                    <Link to="/">{t("productDetails.home")}</Link>
                  </li>
                  <li>
                    <Link to="/products">{t("productDetails.products")}</Link>
                  </li>
                  <li>{product.name}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="section-full content-inner bg-white">
          <div className="container woo-entry">
            <div className="row m-b30">
              <div className="col-md-5 col-lg-5 col-sm-12 m-b30">
                <ProductGallerySlider
                  images={product.images}
                  loadedImages={loadedImages}
                  onImageLoad={handleImageLoad}
                />
              </div>
              <div className="col-md-7 col-lg-7 col-sm-12">
                <form method="post" className="cart sticky-top">
                  <div className="dlab-post-title">
                    <h4 className="post-title">{product.name}</h4>
                    <h4 className="item-price">
                      <span className="text-primary">{product.reference}</span>
                    </h4>
                    <p className="m-b10">{product.description}</p>
                    <div className="dlab-divider bg-gray tb15">
                      <i className="icon-dot c-square"></i>
                    </div>
                  </div>

                  {product.category && (
                    <div className="shop-item-tage">
                      <span>{t("productDetails.category")} :- </span>
                      {typeof product.category === "object"
                        ? product.category.name
                        : product.category}
                    </div>
                  )}

                  <div className="dlab-divider bg-gray tb15">
                    <i className="icon-dot c-square"></i>
                  </div>

                  {(product.characteristics.seau_iml ||
                    product.characteristics.seau_offset ||
                    product.characteristics.couvercle_iml ||
                    product.characteristics.couvercle_offset ||
                    isMetallique) && (
                    <div className="m-b30">
                      <h5>{t("productDetails.decorationTypes")}</h5>
                      <br />

                      {isMetallique ? (
                        <div style={decorationDetailsStyle}>
                          <h6>{t("productDetails.decoration")}:</h6>
                          <span>
                            <h6 style={headingStyle}>
                              {t("productDetails.metalPrinted")}
                            </h6>
                          </span>
                        </div>
                      ) : (
                        <>
                          {(product.characteristics.seau_offset?.label ||
                            product.characteristics.seau_iml?.label) && (
                            <div style={decorationDetailsStyle}>
                              <h6>{t("productDetails.bucket")}</h6>
                              {product.characteristics.seau_offset?.label && (
                                <span>
                                  <h6 style={headingStyle}>
                                    {t("productDetails.offset")}:
                                  </h6>{" "}
                                  {product.characteristics.seau_offset?.label}
                                </span>
                              )}
                              {product.characteristics.seau_iml?.label && (
                                <span>
                                  <h6 style={headingStyle}>
                                    {t("productDetails.iml")}:
                                  </h6>{" "}
                                  {product.characteristics.seau_iml?.label}
                                </span>
                              )}
                            </div>
                          )}
                          <br />
                          {(product.characteristics.couvercle_offset?.label ||
                            product.characteristics.couvercle_iml?.label) && (
                            <div style={decorationDetailsStyle}>
                              <h6>{t("productDetails.lid")}</h6>
                              {product.characteristics.couvercle_offset
                                ?.label && (
                                <span>
                                  <h6 style={headingStyle}>
                                    {t("productDetails.offset")}:
                                  </h6>{" "}
                                  {
                                    product.characteristics.couvercle_offset
                                      ?.label
                                  }
                                </span>
                              )}
                              {product.characteristics.couvercle_iml?.label && (
                                <span>
                                  <h6 style={headingStyle}>
                                    {t("productDetails.iml")}:
                                  </h6>{" "}
                                  {product.characteristics.couvercle_iml?.label}
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </form>
              </div>
            </div>

            <div className="row m-b30">
              <div className="col-lg-12">
                <div className="dlab-tabs product-description tabs-site-button">
                  <Tabs>
                    <TabList className="nav nav-tabs">
                      <Tab selectedClassName="tab-active">
                        <Link to="#">
                          <i className="fa fa-photo"></i>{" "}
                          {t("productDetails.characteristics")}
                        </Link>
                      </Tab>
                    </TabList>
                    <TabPanel className="tab-pane">
                      <table className="table table-bordered">
                        <tbody>{renderCharacteristics()}</tbody>
                      </table>
                    </TabPanel>
                  </Tabs>
                </div>
              </div>
            </div>

            <div style={{ marginTop: "20px", textAlign: "center" }}>
              {product?.pdfFiles?.length > 0 ? (
                <button
                  onClick={handleDownloadPDF}
                  style={{
                    backgroundColor: "#808080",
                    color: "#ffffff",
                    padding: "10px 20px",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                >
                  <PrintIcon style={{ fill: "#ffffff" }} />
                  {t("productDetails.downloadPdf")}
                </button>
              ) : (
                <span>{t("productDetails.noPdf")}</span>
              )}
            </div>

            <div className="row">
              <div className="col-lg-12">
                <h5 className="m-b20">{t("productDetails.otherProducts")}</h5>
                <ProductSlider
  currentProductId={product._id}
  currentCategory={
    typeof product.category === "object"
      ? product.category.name
      : product.category
  }
/>                </div>
            </div>
          </div>
        </div>
      </div>

      <Footer hideContactInfo />
    </>
  );
};

export default ProductDetails;
